import firebase from 'firebase/compat/app'
import 'firebase/compat/auth'
import UserService from '@/services/api/user.service.js'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { clearDatabase } from '@/utils/db/clear.js'
import { tokenManager } from '@/services/apiClient'
import { cleanupAppSockets } from '@/services/socket/socketInitializer.service.js'
import { clearStores } from '@/utils/clearStores'
import backNav from '@/utils/backNavigationManager.js'
import { detectNavigatorLocale } from '@/i18n.js'
import { useUIStore } from '@/stores/ui.store'

dayjs.extend(utc)
dayjs.extend(timezone)

/**
 * Validate if an email has correct format
 * @param {string} email - Email to validate
 * @returns {boolean} - Whether email is valid
 */
export const validEmail = (email) => {
    const re =
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    return re.test(email)
}

/**
 * Check if an email is available for registration
 * @param {string} email - Email to check
 * @returns {Promise<boolean>} - True if email is available, false if already registered
 */
export const isUniqueEmail = async (email) => {
    try {
        const response = await UserService.isUniqueEmail(email)
        const responseMessage = response.data.msg
        return responseMessage === 'email is available'
    } catch (error) {
        console.error('Error checking email uniqueness:', error)
        // Consider if returning true on error is the desired behavior.
        // It might allow registration attempts that will fail later.
        // Returning false might be safer but could block valid registrations if the check fails.
        return false // Changed to false on error to prevent proceeding if check fails
    }
}

/**
 * Send password reset email
 * @param {string} email - Email to send reset link to
 * @returns {Promise<boolean>} - True if successful, false otherwise
 */
export const sendPasswordResetEmail = async (email) => {
    try {
        await firebase.auth().sendPasswordResetEmail(email)
        return true
    } catch (error) {
        console.error('Password reset error:', error)
        return false
    }
}

/**
 * Send email verification to current user
 * @param {string} redirectUrl - URL to redirect after verification (optional)
 * @returns {Promise<Object>} - Result with success status and error if applicable
 */
export const sendEmailVerification = async (
    redirectUrl = window.location.origin + '/email-verification'
) => {
    try {
        const user = firebase.auth().currentUser
        if (!user) {
            return { success: false, error: 'No user is currently signed in.' }
        }

        if (user.emailVerified) {
            return { success: false, error: 'Email is already verified.' }
        }

        // Set Firebase Auth language based on user's preferred locale for email verification
        const detectedLocale = detectNavigatorLocale({
            supported: ['es', 'en', 'pt', 'de', 'nl'],
            fallback: 'en',
            storageKey: 'preferredLanguage',
        })
        firebase.auth().languageCode = detectedLocale

        const actionCodeSettings = {
            url: redirectUrl,
            handleCodeInApp: false,
        }

        await user.sendEmailVerification(actionCodeSettings)
        return { success: true }
    } catch (error) {
        console.error('Send verification email error:', error)
        let errorMessage = 'Failed to send verification email.'
        if (error.code === 'auth/too-many-requests') {
            errorMessage =
                'Too many verification emails sent. Please wait before trying again.'
        }
        return { success: false, error: errorMessage }
    }
}

/**
 * Reload current user's auth state to check email verification status
 * @returns {Promise<Object>} - Result with success status and emailVerified boolean
 */
export const reloadUserAuthState = async () => {
    try {
        const user = firebase.auth().currentUser
        if (!user) {
            return {
                success: false,
                error: 'No user is currently signed in.',
            }
        }

        await user.reload()
        const updatedUser = firebase.auth().currentUser
        return {
            success: true,
            emailVerified: updatedUser.emailVerified,
        }
    } catch (error) {
        console.error('Reload user auth state error:', error)
        return { success: false, error: 'Failed to reload user state.' }
    }
}

/**
 * Sign in with email and password
 * @param {string} email - User email
 * @param {string} password - User password
 * @returns {Promise<Object>} - Response with user data and status
 */
export const loginWithEmail = async (email, password) => {
    try {
        const response = await firebase
            .auth()
            .signInWithEmailAndPassword(email, password)

        if (response.user) {
            // Fetch user data from backend first to check user type
            const user = await UserService.getCurrentUser()
            // Check if backend returned a user successfully
            if (user && user.status === 200 && user.data) {
                // NOTE: Email verification for creators is temporarily disabled
                // Uncomment the block below to re-enable
                // Check if Creator user needs email verification (only for email/password auth)
                // if (
                //     user.data.typeofuser === 'Creator' &&
                //     !response.user.emailVerified
                // ) {
                //     return {
                //         success: true,
                //         requiresEmailVerification: true,
                //         email: response.user.email,
                //         user: user.data,
                //     }
                // }
                return { success: true, user: user.data, status: user.status }
            } else {
                // Handle cases where backend doesn't find the user or returns an error
                console.warn(
                    'Backend user not found or error during login:',
                    user
                )
                // Optionally sign out the Firebase user if backend sync is critical
                // await firebase.auth().signOut();
                return {
                    success: false,
                    error: 'Failed to retrieve user data from server.',
                }
            }
        }
        // This case should ideally not be reached if signInWithEmailAndPassword resolves without error
        return {
            success: false,
            error: 'Firebase login did not return a user.',
        }
    } catch (error) {
        console.error('Login error:', error)
        let errorMessage = 'Login failed. Please check your credentials.'
        if (
            error.code === 'auth/user-not-found' ||
            error.code === 'auth/wrong-password' ||
            error.code === 'auth/invalid-credential'
        ) {
            errorMessage = 'Invalid email or password.'
        } else if (
            error.message === 'Failed to retrieve user data from server.'
        ) {
            errorMessage = error.message
        }
        return { success: false, error: errorMessage }
    }
}

/**
 * Register new user with email and password
 * @param {string} email - User email
 * @param {string} password - User password
 * @param {string} verificationRedirectUrl - URL to redirect after email verification (optional)
 * @returns {Promise<Object>} - Response with status
 */
export const signupWithEmail = async (
    email,
    password,
    verificationRedirectUrl = window.location.origin + '/email-verification'
) => {
    let firebaseUser = null // Keep track of the created Firebase user
    try {
        // 1. Create user in Firebase
        const userCredential = await firebase
            .auth()
            .createUserWithEmailAndPassword(email, password)
        firebaseUser = userCredential.user // Store the user object

        if (!firebaseUser) {
            // This case should be rare if createUserWithEmailAndPassword resolves
            throw new Error(
                'Firebase user creation succeeded but no user object was returned.'
            )
        }

        const timezone = dayjs.tz.guess()
        // 2. Sign up user in backend
        const newUserResponse = await UserService.SignupFirebase(
            email,
            firebaseUser.uid,
            timezone
        )

        // 3. Check backend response
        // Assuming 201 is the success status for backend creation
        if (newUserResponse && newUserResponse.status === 201) {
            // NOTE: Email verification for creators is temporarily disabled
            // Uncomment the block below to re-enable
            // 4. Send email verification after successful signup (only for Creator users)
            // if (newUserResponse.data?.typeofuser === 'Creator') {
            //     try {
            //         // Set Firebase Auth language based on user's preferred locale for email verification
            //         const detectedLocale = detectNavigatorLocale({
            //             supported: ['es', 'en', 'pt', 'de', 'nl'],
            //             fallback: 'en',
            //             storageKey: 'preferredLanguage',
            //         })
            //         firebase.auth().languageCode = detectedLocale
            //
            //         const actionCodeSettings = {
            //             url: verificationRedirectUrl,
            //             handleCodeInApp: false,
            //         }
            //
            //         await firebaseUser.sendEmailVerification(actionCodeSettings)
            //         console.log(
            //             'Verification email sent successfully to Creator'
            //         )
            //     } catch (verificationError) {
            //         // Log the error but don't fail the signup
            //         console.error(
            //             'Failed to send verification email:',
            //             verificationError
            //         )
            //         // We still return success since the user account was created
            //     }
            // }

            return {
                success: true,
                status: newUserResponse.status,
                data: newUserResponse.data,
            }
        } else {
            // Backend signup failed, throw an error to trigger cleanup
            console.error('Backend signup failed:', newUserResponse)
            throw new Error('Failed to create user account on server.')
        }
    } catch (error) {
        console.error('Signup error:', error)

        // Attempt to delete Firebase user ONLY if it was created and the error occurred afterwards
        if (
            firebaseUser &&
            error.message === 'Failed to create user account on server.'
        ) {
            console.warn(
                'Backend signup failed after Firebase user creation. Attempting to delete Firebase user...'
            )
            try {
                await firebaseUser.delete()
                console.log(
                    'Firebase user deleted successfully after backend failure.'
                )
            } catch (deleteError) {
                console.error(
                    'CRITICAL: Failed to delete Firebase user after backend signup failure. Account may be in inconsistent state.',
                    deleteError
                )
                // Return a specific error indicating the cleanup failed
                return {
                    success: false,
                    error: 'Signup failed, and automatic cleanup could not complete. Please contact support.',
                }
            }
        }

        // Determine the final error message based on the original error
        let errorMessage = 'An unexpected error occurred during signup.'
        if (error.code === 'auth/email-already-in-use') {
            errorMessage = 'This email address is already registered.'
        } else if (error.code === 'auth/weak-password') {
            errorMessage = 'The password is too weak.'
        } else if (
            error.message === 'Failed to create user account on server.'
        ) {
            // Use the specific backend error message if available, otherwise the generic one
            errorMessage = error.message
        } else if (
            error.message.includes(
                'Firebase user creation succeeded but no user object'
            )
        ) {
            errorMessage =
                'An internal error occurred during signup (Firebase user missing).'
        }
        // Ensure we don't return the "cleanup failed" message if the original error was different
        if (
            errorMessage !==
            'Signup failed, and automatic cleanup could not complete. Please contact support.'
        ) {
            return { success: false, error: errorMessage }
        } else {
            // If the cleanup failed error was already set, return that specific one.
            return { success: false, error: errorMessage }
        }
    }
}

/**
 * Sign in or register with OAuth provider (Google/Apple)
 * @param {function} providerFn - Function that returns an OAuth provider instance
 * @returns {Promise<Object>} - Response with status and user data
 */
export const signInWithProvider = async (providerFn) => {
    try {
        const timezone = dayjs.tz.guess()
        const provider = providerFn() // Get provider instance
        const result = await firebase.auth().signInWithPopup(provider)
        const currentUser = result.user
        if (!currentUser) {
            throw new Error(
                'Firebase sign-in succeeded but no user data received.'
            )
        }
        const idToken = await currentUser.getIdToken(true)

        // For Apple Sign-in: handle privaterelay email
        let email = currentUser.email || ''
        if (email.includes('@privaterelay.appleid.com')) {
            email = '' // Treat private relay email as empty for backend signup purposes
        }

        // Call backend to log in or create user if missing
        // This assumes ContinueWith handles both login and potential signup on backend
        const backendResponse = await UserService.ContinueWith(
            idToken,
            currentUser.displayName,
            email,
            currentUser.uid,
            timezone
        )

        // Check backend response status (expecting 200 for login, 201 for signup)
        if (
            backendResponse &&
            (backendResponse.status === 200 || backendResponse.status === 201)
        ) {
            // Ensure data structure from backend is consistent
            const isNewUser =
                backendResponse.status === 201 ||
                backendResponse.data?.action === 'signup'
            const userData = backendResponse.data?.user || backendResponse.data // Adapt based on backend response structure

            if (!userData) {
                console.error(
                    'Backend response successful but missing user data:',
                    backendResponse
                )
                throw new Error(
                    'Backend account synchronization failed: Missing user data.'
                )
            }

            return {
                success: true,
                status: backendResponse.status,
                data: userData,
                isNew: isNewUser,
            }
        } else {
            // Backend indicated an issue or returned unexpected status
            console.error('Backend OAuth login/sync error:', backendResponse)
            // Attempt to sign the user out of Firebase to prevent inconsistent state
            await firebase.auth().signOut()
            throw new Error('Backend account synchronization failed.')
        }
    } catch (error) {
        console.error('OAuth sign in error:', error)

        // Handle user closing popup
        if (
            error.code === 'auth/popup-closed-by-user' ||
            error.code === 'auth/cancelled-popup-request'
        ) {
            return { success: false, userCancelled: true }
        }

        // Translate error message
        let errorMessage =
            error.message || 'An unknown error occurred during sign-in.'
        if (error.code === 'auth/account-exists-with-different-credential') {
            errorMessage =
                'An account already exists with the same email address but different sign-in credentials. Try signing in with the original method.'
        } else if (
            error.message.startsWith('Backend account synchronization failed')
        ) {
            errorMessage = error.message // Use the specific backend error
        }

        return { success: false, error: errorMessage }
    }
}

// Create provider instances
export const googleProvider = () => new firebase.auth.GoogleAuthProvider()
export const appleProvider = () => {
    const provider = new firebase.auth.OAuthProvider('apple.com')
    provider.addScope('email')
    provider.addScope('name')
    return provider
}

export const xProvider = () => {
    const provider = new firebase.auth.TwitterAuthProvider()
    return provider
}

/**
 * Sign in anonymously
 * @returns {Promise<Object>} - Response with status and user data
 */
export const signInAnonymously = async () => {
    try {
        const result = await firebase.auth().signInAnonymously()
        return { success: true, user: result.user }
    } catch (error) {
        console.error('Anonymous sign in error:', error)
        return { success: false, error: error.message }
    }
}

/**
 * Link anonymous account to credential (email/password or OAuth)
 * @param {Object} credential - The credential to link
 * @returns {Promise<Object>} - Response with status and user data
 */
export const linkAnonymousAccount = async (credential) => {
    try {
        const user = firebase.auth().currentUser
        if (!user) {
            throw new Error('No user currently signed in')
        }

        const result = await user.linkWithCredential(credential)
        return { success: true, user: result.user }
    } catch (error) {
        console.error('Account linking error:', error)
        // If credential already exists (linked to another account), we might need to handle it
        return { success: false, error: error.message, code: error.code }
    }
}

/**
 * Performs all necessary cleanup operations when a user logs out or session ends.
 * This includes cleaning up sockets, stores, database, and local storage.
 */
export const performLogoutCleanup = async () => {
    console.log('Performing logout cleanup...')

    // 1. Clear back navigation contexts to prevent interference with redirection
    backNav.clear()

    // 2. Clean up sockets
    try {
        await cleanupAppSockets()
        console.log('Socket cleanup completed')
    } catch (error) {
        console.error('Error during socket cleanup:', error)
    }

    // 3. Clean all Pinia stores
    try {
        clearStores()
        console.log('Store cleanup completed')
    } catch (error) {
        console.error('Error during store cleanup:', error)
    }

    // 4. Clean IndexedDB
    try {
        await clearDatabase()
        console.log('Database cleanup completed')
    } catch (error) {
        console.error('Error during database cleanup:', error)
    }

    // 5. Unregister notifications and reset state
    try {
        const { useNotifications } = await import(
            '@/composables/useNotifications'
        )
        const { logoutNotifications } = useNotifications()
        await logoutNotifications()
        console.log('Notification cleanup completed')
    } catch (error) {
        console.error('Error during notification cleanup:', error)
    }
    // 6. Clear the token manager
    try {
        tokenManager.resetTokenManager()
        console.log('Token manager reset completed')
    } catch (error) {
        console.error('Error resetting token manager:', error)
    }

    // 6. Clear session storage items related to onboarding
    sessionStorage.removeItem('onboardingUserType')

    // 7. Clear specific local storage items including persisted auth store
    const blackListedKeys = ['lastTokenSync', 'authUser'] // Keys to be deleted
    blackListedKeys.forEach((key) => {
        localStorage.removeItem(key)
    })

    console.log('Logout cleanup process completed')
}

// Function to logout
export const logout = async () => {
    console.log('Starting logout process...')

    // 1. Close navbar
    const uiStore = useUIStore()
    uiStore.setMenuOpen(false)

    // Logout from Firebase. This will trigger onAuthStateChanged in App.vue
    // where all the cleanup logic will be executed via performLogoutCleanup.
    if (firebase.auth().currentUser) {
        try {
            await firebase.auth().signOut()
            console.log('Firebase signout completed')
        } catch (error) {
            console.error('Error signing out from Firebase:', error)
        }
    } else {
        console.log(
            'Logout called but no user was signed in. Running cleanup anyway.'
        )
        // If no user is signed in but logout is triggered, we should still clean up
        // to handle any inconsistent state.
        await performLogoutCleanup()
    }
}
